/**
 * Google Photos read-only metadata. Used by Almari to auto-import closet
 * photos. Pixel fetch happens client-side only -- the server never sees them.
 *
 * Filter strategy: search albums named "Closet" / "Wardrobe" / "Almari",
 * or default to media filter contentCategory=PEOPLE inverted (we want
 * isolated garment shots, not selfies).
 */
import type { PhotoSlim } from './types.ts';
import { MOCK_PHOTOS } from './mock-data.ts';
import { ensureFreshToken, gmailFetch, type OAuthToken, GmailOAuth } from './gmail-oauth.ts';

const SEARCH_URL = 'https://photoslibrary.googleapis.com/v1/mediaItems:search';
const ALBUMS_URL = 'https://photoslibrary.googleapis.com/v1/albums';

interface MediaItem {
  id: string;
  mimeType: string;
  filename?: string;
  baseUrl: string;
  mediaMetadata?: {
    creationTime?: string;
    photo?: { cameraModel?: string };
    width?: string;
    height?: string;
  };
}
interface SearchResp {
  mediaItems?: MediaItem[];
  nextPageToken?: string;
}

export interface PhotosOpts {
  readonly mode: 'mock' | 'live';
  readonly oauth?: GmailOAuth;
  readonly token?: OAuthToken;
  readonly album_hints?: readonly string[];
  readonly since_days?: number;
  readonly limit?: number;
}

export async function fetchClosetPhotos(opts: PhotosOpts): Promise<readonly PhotoSlim[]> {
  if (opts.mode === 'mock') return MOCK_PHOTOS;
  if (!opts.oauth || !opts.token) throw new Error('live mode requires oauth + token');

  const token = await ensureFreshToken(opts.oauth, opts.token);
  const album_hints = opts.album_hints ?? ['closet', 'wardrobe', 'almari'];
  const limit = opts.limit ?? 200;

  // Try named-album path first; fall back to date-range search.
  const albumIds = await findClosetAlbums(token, album_hints);
  const items: PhotoSlim[] = [];

  if (albumIds.length > 0) {
    for (const albumId of albumIds) {
      let pageToken: string | undefined;
      while (items.length < limit) {
        const r = await gmailFetch<SearchResp>(token, SEARCH_URL, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ albumId, pageSize: Math.min(100, limit - items.length), pageToken }),
        });
        for (const it of r.mediaItems ?? []) items.push(toSlim(it));
        pageToken = r.nextPageToken;
        if (!pageToken) break;
      }
    }
    return items;
  }

  // No named album: pull recent IMAGE items, exclude PEOPLE-only.
  const since = opts.since_days ?? 1095;
  const startDate = new Date(Date.now() - since * 86_400_000);
  const filters = {
    mediaTypeFilter: { mediaTypes: ['PHOTO'] },
    dateFilter: {
      ranges: [
        {
          startDate: ymd(startDate),
          endDate: ymd(new Date()),
        },
      ],
    },
    contentFilter: { excludedContentCategories: ['SELFIES', 'PEOPLE'] },
  };
  let pageToken: string | undefined;
  while (items.length < limit) {
    const r = await gmailFetch<SearchResp>(token, SEARCH_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ pageSize: Math.min(100, limit - items.length), pageToken, filters }),
    });
    for (const it of r.mediaItems ?? []) items.push(toSlim(it));
    pageToken = r.nextPageToken;
    if (!pageToken) break;
  }
  return items;
}

async function findClosetAlbums(token: OAuthToken, hints: readonly string[]): Promise<string[]> {
  const u = new URL(ALBUMS_URL);
  u.searchParams.set('pageSize', '50');
  const r = await gmailFetch<{ albums?: ReadonlyArray<{ id: string; title: string }> }>(
    token,
    u.toString(),
  );
  const out: string[] = [];
  for (const a of r.albums ?? []) {
    if (hints.some((h) => a.title.toLowerCase().includes(h))) out.push(a.id);
  }
  return out;
}

interface YearMonthDay { year: number; month: number; day: number; }
function ymd(d: Date): YearMonthDay {
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

function toSlim(it: MediaItem): PhotoSlim {
  const baseObj: PhotoSlim = {
    external_id: it.id,
    fetched_url: `${it.baseUrl}=w1024-h1024`,
    mime: it.mimeType,
    provenance: { connector: 'gphotos', source_id: it.id, fetched_at: Date.now() },
  };
  const capturedAt = it.mediaMetadata?.creationTime;
  const cameraModel = it.mediaMetadata?.photo?.cameraModel;
  return {
    ...baseObj,
    ...(capturedAt ? { captured_at: capturedAt } : {}),
    ...(cameraModel ? { camera_model: cameraModel } : {}),
  };
}
